/* eslint-disable camelcase */
import type { TranscriptEntry } from './artifactDerivations'

export const LIVE_PROVIDER_FALLBACK_REASONS = [
  'translation',
  'hipaa',
  'not_configured',
  'breaker_open',
  'connect_failed',
  'handshake_rejected',
  'handshake_timeout',
  'capacity',
] as const

export type LiveProviderProvenance = {
  provider: 'soniox' | 'muse'
  relay: 'v1' | 'v2'
  fallbackReason?: (typeof LIVE_PROVIDER_FALLBACK_REASONS)[number] | 'unknown'
}

export function parseLiveProvider(value: unknown): LiveProviderProvenance | undefined {
  if (typeof value !== 'object' || value === null) return undefined
  const raw = value as Record<string, unknown>
  if (raw.provider !== 'soniox' && raw.provider !== 'muse') return undefined
  if (raw.relay !== 'v1' && raw.relay !== 'v2') return undefined
  if (raw.provider === 'muse' && raw.relay !== 'v2') return undefined
  const reason = LIVE_PROVIDER_FALLBACK_REASONS.find((candidate) => candidate === raw.fallbackReason)
  if (
    raw.fallbackReason !== undefined &&
    (typeof raw.fallbackReason !== 'string' || raw.provider !== 'soniox' || raw.relay !== 'v2')
  )
    return undefined
  return {
    provider: raw.provider,
    relay: raw.relay,
    ...(raw.fallbackReason !== undefined ? { fallbackReason: reason ?? 'unknown' } : {}),
  }
}

const providerName = (provider: LiveProviderProvenance['provider']) => (provider === 'muse' ? 'Muse' : 'Soniox')

export function liveProviderLabel(entries: readonly TranscriptEntry[], prefix = 'Live'): string {
  const provenances: (LiveProviderProvenance | undefined)[] = []
  for (const entry of entries) {
    if (!('speaker' in entry)) continue
    provenances.push(entry.speaker.source === 'refined' ? undefined : parseLiveProvider(entry.liveProvider))
  }
  return summarizeLiveProviders(provenances, prefix)
}

export function summarizeLiveProviders(
  provenances: readonly (LiveProviderProvenance | undefined)[],
  prefix = 'Live'
): string {
  const providers = new Set<string>()
  let hasUnknown = false
  for (const provenance of provenances) {
    if (provenance) providers.add(providerName(provenance.provider))
    else hasUnknown = true
  }
  if (providers.size === 0) return `${prefix} · provider unknown`
  const names = Array.from(providers).sort().join(' + ')
  return `${prefix} · ${names}${hasUnknown ? ' + unknown' : ''}`
}

const fallbackLabels: Record<NonNullable<LiveProviderProvenance['fallbackReason']>, string> = {
  translation: 'translation requested',
  hipaa: 'HIPAA BAA (Muse not covered)',
  not_configured: 'Muse not configured',
  breaker_open: 'Muse circuit open',
  connect_failed: 'Muse connection failed',
  handshake_rejected: 'Muse handshake rejected',
  handshake_timeout: 'Muse handshake timed out',
  capacity: 'Muse capacity reached',
  unknown: 'reason unknown',
}

export function liveProviderDetail(value: unknown): string {
  return describeLiveProvider(parseLiveProvider(value))
}

export function describeLiveProvider(provenance: LiveProviderProvenance | undefined): string {
  if (!provenance) return 'Provider unknown'
  const provider = `${providerName(provenance.provider)} · relay ${provenance.relay}`
  return provenance.fallbackReason ? `${provider} · fallback: ${fallbackLabels[provenance.fallbackReason]}` : provider
}
